import { ref, watch } from 'vue';
import { createSharedComposable, useDebounceFn } from '@vueuse/core';

const emptyResults = { clients: [], vehicles: [], invoices: [] };

function _useGlobalSearch() {
    const open = ref(false);
    const query = ref('');
    const results = ref({ ...emptyResults });
    const loading = ref(false);

    let activeController = null;

    const abortActiveRequest = () => {
        if (activeController) {
            activeController.abort();
            activeController = null;
        }
    };

    const openSearch = () => {
        open.value = true;
    };

    const closeSearch = () => {
        abortActiveRequest();
        open.value = false;
        query.value = '';
        results.value = { ...emptyResults };
        loading.value = false;
    };

    const performSearch = useDebounceFn(async (searchQuery) => {
        abortActiveRequest();

        if (!searchQuery || searchQuery.length < 2) {
            results.value = { ...emptyResults };
            loading.value = false;

            return;
        }

        const controller = new AbortController();
        activeController = controller;
        loading.value = true;

        try {
            const response = await window.axios.get(route('api.search'), {
                params: { query: searchQuery },
                signal: controller.signal,
            });

            if (activeController !== controller) {
                return;
            }

            results.value = response.data;
        } catch (error) {
            if (window.axios.isCancel?.(error) || error?.name === 'CanceledError' || error?.name === 'AbortError') {
                return;
            }

            if (activeController !== controller) {
                return;
            }

            results.value = { ...emptyResults };
        } finally {
            if (activeController === controller) {
                activeController = null;
                loading.value = false;
            }
        }
    }, 250);

    watch(query, (value) => {
        if (value.length >= 2) {
            loading.value = true;
        }

        performSearch(value);
    });

    return {
        open,
        query,
        results,
        loading,
        openSearch,
        closeSearch,
    };
}

export const useGlobalSearch = createSharedComposable(_useGlobalSearch);
